import { useDisclosure } from '@chakra-ui/react';
import BrandKitImage from 'assets/images/brand_kit.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { AUTO_BRAND_OPENED, CLICK_ON_PAID_FEATURE, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { UpgradeHover } from 'modules/Editor/components/UpgradeHover';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store/hooks';
import { selectColorSwatch } from 'modules/Editor/store/infographSelector';
import { removeSwatchColor, setSwatchColor } from 'modules/Editor/store/infographSlice';
import { UserTypes, useBrand, useUser } from 'modules/User';
import { BrandOptionDropdown } from 'modules/User/components/BrandOptionDropdown';
import { ColorPicker as BaseColorPicker } from 'modules/common/components/ColorPicker';
import {
  FAIL_SATURATION_BORDER_COLOR,
  PASS_SATURATION_BORDER_COLOR,
} from 'modules/common/components/ColorPicker/ColorPicker.config';
import { ContrastCheckStatus } from 'modules/common/components/ColorPicker/ColorPicker.types';
import { useCallback, useMemo, useState } from 'react';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { AutoBrandCard } from '../AutoBrand';
import { autoBrandModalsIds } from '../AutoBrand/AutoBrand.config';
import { CommonGatedUpgradeModal, commonUpgradeModalId } from '../Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from '../Modals/CommonGatedUpgradeModal.types';
import { getBrandColorPalette, serializeLogoData } from '../WidgetMenu/SideMenu/BrandKitMenu/BrandKitMenu.helper';
import { validateBrands } from './ColorPicker.helper';
import { EditorColorPickerProps } from './ColorPicker.types';
import { SingleColorContrastChecker } from './SingleColorContrastChecker';

// Extend the ColorPicker with editor state
export const ColorPicker = ({
  color,
  label,
  iconStyle,
  onChange,
  onContainerBlur,
  showBrandSection = true,
  showNoColorOption,
  showNoColorChecker,
  showOpacityOption,
  placement,
  colorCheckerProps,
  hasArrow,
  onOpen,
  onClose,
  iconColors,
  h,
  w,
}: EditorColorPickerProps) => {
  const [contrastCheckStatus, setContrastCheckStatus] = useState<ContrastCheckStatus>(ContrastCheckStatus.Disabled);
  const colorSwatch = useAppSelector(selectColorSwatch);
  const brandIdx = useAppSelector(selectBrandIdx);
  const dispatch = useAppDispatch();
  const { trackChameleon } = useChameleon();

  const { data: brandsData } = useBrand();
  const {
    isAdminUser,
    isBusinessUser,
    isEnterpriseUser,
    isTeamOwnerRole,
    isTeamAdminRole,
    isPremiumUser,
    isFreeUser,
    isEducationUser,
    isGuestRole,
  } = useUser();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  const onSetSwatchColor = useCallback((swatchColor: string) => dispatch(setSwatchColor({ swatchColor })), [dispatch]);
  const onRemoveSwatchColor = useCallback(
    (swatchColor: string) => dispatch(removeSwatchColor({ swatchColor })),
    [dispatch],
  );

  const hasBrandSection =
    (isBusinessUser && isTeamOwnerRole) ||
    (isEnterpriseUser && isTeamAdminRole) ||
    isFreeUser ||
    isPremiumUser ||
    isAdminUser;

  const hasBrandPermission = (isBusinessUser && !isGuestRole) || (isEnterpriseUser && !isGuestRole) || isAdminUser;
  const { colors: indexedColors } = useMemo(() => validateBrands(brandsData ?? []), [brandsData]);
  const brandColors = indexedColors[brandIdx]?.flat();
  const brandLogos = brandsData?.[brandIdx]?.logos || [];
  const serializedLogoData = serializeLogoData({ data: brandLogos });
  const brandColorPalettes = useMemo(() => {
    return brandsData && brandsData.length > 0 ? getBrandColorPalette(brandsData[brandIdx]) : [];
  }, [brandIdx, brandsData]);

  const hasColorPalettes = brandsData && brandColorPalettes.length > 0;
  const showLogos = !!serializedLogoData.length;
  const hasNoBrand = !hasColorPalettes && !showLogos;

  // Border colour to apply to the saturation picker from react-colorful lib
  // Used to indicate whether the selected colour passes or fails the contrast checker
  const saturationBorderColor = useMemo(() => {
    let color;
    switch (contrastCheckStatus) {
      case ContrastCheckStatus.Pass:
        color = PASS_SATURATION_BORDER_COLOR;
        break;
      case ContrastCheckStatus.Fail:
        color = FAIL_SATURATION_BORDER_COLOR;
        break;
      default:
        break;
    }

    return color;
  }, [contrastCheckStatus]);

  // Update the contrast checker status
  const handleContrastCheckUpdate = useCallback(
    ({ isAA, isAAA, hasScore }: { isAA: boolean; isAAA: boolean; hasScore: boolean }) => {
      // Disable if no score
      if (!hasScore) {
        setContrastCheckStatus(ContrastCheckStatus.Disabled);
      } else if (isAA || isAAA) {
        // Contrast check passes
        setContrastCheckStatus(ContrastCheckStatus.Pass);
      } else {
        // Contrast check fails
        setContrastCheckStatus(ContrastCheckStatus.Fail);
      }
    },
    [],
  );

  const onUpgradeClick = useCallback(() => {
    Mixpanel.track(UPGRADE.eventName, {
      feature: UPGRADE.feature.BrandkitColor,
      plan: UserTypes.Business,
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.BrandkitColor);
    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
  }, [trackChameleon]);

  const onClickUpgradeModal = () => {
    onOpenUpgradeModal();
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.BrandkitColor,
      plan: UserTypes.Business,
    });
  };

  return (
    <>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onUpgradeClick}
        upgradePlan={UserTypes.Business}
        modalImg={BrandKitImage}
        modalType={ModalUpgradeTypeEnum.BrandKit}
        modalContentProps={{ zIndex: 'tooltip' }} // define a high zIndex compared to popover
      />
      <BaseColorPicker
        color={color}
        iconStyle={iconStyle}
        onChange={onChange}
        onContainerBlur={onContainerBlur}
        showBrandSection={!isEducationUser && (!hasNoBrand || hasBrandSection) && showBrandSection}
        label={label}
        colorSwatch={colorSwatch}
        showNoColorOption={showNoColorOption}
        onSetSwatchColor={onSetSwatchColor}
        onRemoveSwatchColor={onRemoveSwatchColor}
        placement={placement}
        saturationBorderColor={saturationBorderColor}
        hasArrow={hasArrow}
        onClose={onClose}
        onOpen={onOpen}
        brandColors={brandColors}
        skipOutsideOnCloseIds={[...autoBrandModalsIds, commonUpgradeModalId]}
        brandSwitchMenu={!hasBrandPermission ? undefined : <BrandOptionDropdown enableIfOnlyOneBrand />}
        iconColors={iconColors}
        brandUpgrade={
          hasBrandPermission ? undefined : (
            <UpgradeHover
              onClick={onClickUpgradeModal}
              upgradeType={UserTypes.Business}
              data-testid='brand-color-upgrade-hover'
            />
          )
        }
        hasBrandPermission={hasBrandPermission}
        h={h}
        w={w}
        showOpacityOption={showOpacityOption}
        brandComponent={
          !brandLogos?.length && !brandColors?.length ? (
            <AutoBrandCard mixpanelFrom={AUTO_BRAND_OPENED.from.colorMenu} type='button' />
          ) : null
        }
      >
        {!showNoColorChecker && (
          <SingleColorContrastChecker color={color} onUpdate={handleContrastCheckUpdate} {...colorCheckerProps} />
        )}
      </BaseColorPicker>
    </>
  );
};
